"use client";

import Link from "next/link";
import { Link as IntlLink } from '@/i18n/navigation';
import { Menu } from "lucide-react";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useTranslations, useLocale } from "next-intl";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import ReactCountryFlag from "react-country-flag";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuViewport,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useState } from "react";
import { usePathname, useRouter } from "next/navigation";

export default function HeaderNav() {
  const t = useTranslations("common");
  const isMobile = useIsMobile();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  const goToSection = (id: string) => {
    setOpen(false);
    const homePath = `/${locale}/`;
    const targetUrl = `${homePath}#${id}`;
    const onHome = pathname === homePath;

    setTimeout(() => {
      if (!onHome) {
        router.push(targetUrl);
        return;
      }

      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        history.replaceState(null, "", `#${id}`);
      } else {
        router.push(`#${id}`);
      }
    }, 50);
  };

  const locales = [
    { code: 'ru', name: 'Русский' },
    { code: 'ro', name: 'Română' },
    { code: 'en', name: 'English' }
  ];

  const currentLocale = locales.find(l => l.code === locale) || locales[0];

  if (isMobile) {
    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="px-2.5">
              <ReactCountryFlag
                countryCode={currentLocale.code === "en" ? "GB" : (currentLocale.code?.toUpperCase() || "GB")}
                svg
                style={{ width: "1.3em", height: "1.3em", borderRadius: 3 }}
                aria-label={currentLocale.name}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-45">
            {locales.map((localeOption) => (
              <DropdownMenuItem key={localeOption.code} className="p-0">
                <IntlLink
                  href="/"
                  locale={localeOption.code}
                  className={`flex items-center gap-3 w-full p-2 rounded-sm hover:bg-accent transition-colors ${locale === localeOption.code ? 'bg-accent text-accent-foreground' : ''}`}
                >
                  <ReactCountryFlag
                    countryCode={localeOption.code === "en" ? "GB" : (localeOption.code?.toUpperCase() || "GB")}
                    svg
                    style={{ width: "1.3em", height: "1.3em", borderRadius: 3 }}
                    aria-label={localeOption.name}
                  />
                  <span className="text-sm">{localeOption.name}</span>
                  {locale === localeOption.code && (
                    <span className="ml-auto text-brand">✓</span>
                  )}
                </IntlLink>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[88vw] sm:w-95">
            <SheetHeader className="text-left">
              <SheetTitle>{t("menu")}</SheetTitle>
            </SheetHeader>
            <nav className="mt-4 grid gap-2 p-2">
              <Link href="/" className="px-2 py-2 rounded-md hover:bg-accent" onClick={() => setOpen(false)}>
                {t("home")}
              </Link>
              <button className="text-left px-2 py-2 rounded-md hover:bg-accent" onClick={() => goToSection("diseases")}>
                {t("diseases")}
              </button>
              <button className="text-left px-2 py-2 rounded-md hover:bg-accent" onClick={() => goToSection("services")}>
                {t("services")}
              </button>
              <button className="text-left px-2 py-2 rounded-md hover:bg-accent" onClick={() => goToSection("doctors")}>
                {t("specialists")}
              </button>
            </nav>
            <SheetFooter>
              <Button className="bg-brand hover:bg-accent-blue"
                onClick={() => {
                  router.push('/prices');
                  setOpen(false);
                }}
              >
                {t("button_price_list")}
              </Button>
              <SheetClose asChild>
                <Button variant="outline">{t("button_close")}</Button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <div className="flex items-center">
      <NavigationMenu className="relative z-40">
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link href="/">
                {t('home')}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
          <NavigationMenuItem>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link href="#diseases">{t('diseases')}</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
          <NavigationMenuItem>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link href="#doctors">{t('specialists')}</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
          <NavigationMenuItem>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link href="#services">{t('services')}</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
        </NavigationMenuList>
        <NavigationMenuViewport className="z-50" />
      </NavigationMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-2 ml-4">
            <ReactCountryFlag
              countryCode={currentLocale.code === "en" ? "GB" : (currentLocale.code?.toUpperCase() || "GB")}
              svg
              style={{ width: "1.3em", height: "1.3em", borderRadius: 3 }}
              aria-label={currentLocale.name}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-45">
          {locales.map((localeOption) => (
            <DropdownMenuItem key={localeOption.code} className="p-0">
              <IntlLink
                href="/"
                locale={localeOption.code}
                className={`flex items-center gap-3 w-full p-2 rounded-sm hover:bg-accent transition-colors ${locale === localeOption.code ? 'bg-accent text-accent-foreground' : ''}`}
              >
                <ReactCountryFlag
                  countryCode={localeOption.code === "en" ? "GB" : (localeOption.code?.toUpperCase() || "GB")}
                  svg
                  style={{ width: "1.3em", height: "1.3em", borderRadius: 3 }}
                  aria-label={localeOption.name}
                />
                <span className="text-sm">{localeOption.name}</span>
                {locale === localeOption.code && (
                  <span className="ml-auto text-green-600">✓</span>
                )}
              </IntlLink>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
